import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubTour } from '../components/tour/SubTourContext';
import { useTour } from '../components/tour/TourContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle2,
  Circle,
  ChevronRight,
  Rocket,
  PartyPopper,
  Building2,
  Users,
  Truck,
  Settings,
  FileText,
  Calendar,
  Shield,
  Clock,
  Play,
  ArrowRight,
  Compass
} from 'lucide-react';

const GettingStarted = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  const { startSubTour } = useSubTour();
  const { startTour } = useTour();
  
  const [checklistData, setChecklistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingStatus, setOnboardingStatus] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [checklistRes, onboardingRes] = await Promise.all([
        api.get('/onboarding/checklist-status'),
        api.get('/users/me/onboarding')
      ]);
      setChecklistData(checklistRes.data);
      setOnboardingStatus(onboardingRes.data);
    } catch (error) {
      console.error('Failed to fetch getting started data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = (item) => {
    if (item.completed) return;
    
    // Special handling for the onboarding tour
    if (item.id === 'tour') {
      // Navigate to dashboard and start the tour
      navigate('/dashboard');
      // Small delay to ensure navigation completes before starting tour
      setTimeout(() => {
        startTour();
      }, 100);
      return;
    }
    
    if (item.sub_tour) {
      startSubTour(item.sub_tour);
    } else if (item.link) {
      navigate(item.link);
    }
  };

  // Build the getting started items
  const getGettingStartedItems = () => {
    const wizardCompleted = onboardingStatus?.wizard_completed || false;
    const items = checklistData?.items || [];
    
    // Add onboarding wizard as Step 1
    const allItems = [
      {
        id: 'onboarding_wizard',
        title: 'Complete Onboarding',
        description: 'Set up your company profile, add assets, operators, and customers',
        icon: Rocket,
        completed: wizardCompleted,
        link: '/onboarding',
        priority: 0
      },
      ...items.filter(i => !i.optional).map((item, idx) => ({
        ...item,
        title: item.label, // Map label to title
        icon: getIconForItem(item.id),
        priority: idx + 1
      }))
    ];
    
    return allItems;
  };

  const getIconForItem = (id) => {
    const iconMap = {
      'tour': Compass,
      'customers': Building2,
      'equipment': Truck,
      'operators': Users,
      'jobs': Calendar,
      'pricelist': Settings,
      'accounting': FileText,
      'add_customer': Building2,
      'add_equipment': Truck,
      'add_operator': Users,
      'create_job': Calendar,
      'complete_job': FileText,
      'setup_rates': Settings,
      'setup_compliance': Shield,
      'setup_timesheets': Clock
    };
    return iconMap[id] || Circle;
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-secondary rounded w-1/3"></div>
          <div className="h-4 bg-secondary rounded w-1/2"></div>
          <div className="space-y-3 mt-6">
            {[1, 2, 3, 4, 5].map(i => (
              <div key={`skeleton-${i}`} className="h-16 bg-secondary rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const items = getGettingStartedItems();
  const completedCount = items.filter(i => i.completed).length;
  const totalCount = items.length;
  const progressPercent = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;
  const allComplete = completedCount === totalCount;

  return (
    <div className="p-6 max-w-4xl mx-auto" data-testid="getting-started-page">
      {/* Header Section - Fleetio inspired */}
      <div className="mb-8">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Welcome to EQUIPR CORE
            </h1>
            <p className="text-muted-foreground text-lg">
              Complete these steps to get the most out of your account
            </p>
          </div>
          <Badge variant="secondary" className="text-sm px-3 py-1">
            {completedCount} of {totalCount} complete
          </Badge>
        </div>
      </div>

      {/* Progress Section */}
      <Card className="mb-8 bg-card border-border">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="flex-1">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Your progress</span>
                <span className="text-sm font-semibold text-[#ffd108]">{Math.round(progressPercent)}%</span>
              </div>
              <Progress value={progressPercent} className="h-3" />
            </div>
          </div>
          
          {allComplete && (
            <div className="flex items-center gap-3 p-4 bg-green-500/10 rounded-lg border border-green-500/20">
              <PartyPopper className="h-6 w-6 text-green-500" />
              <div>
                <p className="font-semibold text-green-500">All steps complete!</p>
                <p className="text-sm text-muted-foreground">You're all set to use EQUIPR CORE</p>
              </div>
              <Button 
                className="ml-auto btn-volt"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Checklist Items - Card style like Fleetio */}
      <div className="space-y-4">
        {items.map((item, index) => {
          const Icon = item.icon || Circle;
          const isCompleted = item.completed;
          const isNext = !isCompleted && items.slice(0, index).every(i => i.completed);
          
          return (
            <Card 
              key={item.id}
              data-testid={`getting-started-item-${item.id}`}
              className={`border transition-all ${
                isCompleted 
                  ? 'border-green-500/30 bg-green-500/5' 
                  : isNext
                    ? 'border-[#ffd108] bg-[#ffd108]/5 cursor-pointer hover:shadow-lg hover:shadow-[#ffd108]/10'
                    : 'border-border cursor-pointer hover:border-muted-foreground'
              }`}
              onClick={() => !isCompleted && handleItemClick(item)}
            >
              <CardContent className="p-5">
                <div className="flex items-center gap-5">
                  {/* Step indicator */}
                  <div className={`flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center ${
                    isCompleted 
                      ? 'bg-green-500' 
                      : isNext 
                        ? 'bg-[#ffd108]' 
                        : 'bg-secondary'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle2 className="h-6 w-6 text-white" />
                    ) : (
                      <Icon className={`h-6 w-6 ${isNext ? 'text-black' : 'text-muted-foreground'}`} />
                    )}
                  </div>
                  
                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <h3 className={`text-lg font-semibold ${
                        isCompleted ? 'text-muted-foreground line-through' : 'text-foreground'
                      }`}>
                        {item.title}
                      </h3>
                      {isNext && (
                        <Badge className="bg-[#ffd108] text-black font-semibold">Up Next</Badge>
                      )}
                    </div>
                    {item.description && (
                      <p className="text-sm text-muted-foreground mt-1">
                        {item.description}
                      </p>
                    )}
                  </div>
                  
                  {/* Action button */}
                  {!isCompleted && (
                    <Button 
                      variant={isNext ? "default" : "outline"} 
                      size="lg"
                      className={isNext ? 'btn-volt' : ''}
                    >
                      {isNext ? (
                        <>
                          <Play className="h-4 w-4 mr-2" />
                          Start
                        </>
                      ) : (
                        <ChevronRight className="h-5 w-5" />
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Help section */}
      <div className="mt-10 text-center">
        <p className="text-muted-foreground">
          Need help? Check out our{' '}
          <button 
            onClick={() => navigate('/help')} 
            className="text-[#ffd108] hover:underline font-medium"
          >
            Help Center
          </button>
          {' '}or{' '}
          <button 
            onClick={() => navigate('/support')} 
            className="text-[#ffd108] hover:underline font-medium"
          >
            Contact Support
          </button>
        </p>
      </div>
    </div>
  );
};

export default GettingStarted;
